/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import {
  Check,
  ChevronDown,
  CircleCheck,
  CircleX,
  Info,
  LoaderCircle,
  Trash2,
  User as UserIcon,
  UserPlus,
  Users,
} from "lucide-react";

import { ThemePalette } from "@src/components/Theme";
import AlertModal from "@src/components/ui/AlertModal";
import CopyMultilineValue from "@src/components/ui/CopyMultilineValue";
import CopyValue from "@src/components/ui/CopyValue";
import {
  ActionButton,
  ActionGroup,
  DetailsActions,
  DetailsPage,
  KeyValueGrid,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import {
  FlexCell,
  ListHeader,
  MenuPanel,
  Pill,
  Portal,
  RowMenu,
  Secondary,
  Stack,
  T9,
  gridTemplate,
  usePopover,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";

import type { Project, RoleAssignment, Role } from "@src/@types/Project";
import type { User } from "@src/@types/User";
import configLoader from "@src/utils/Config";

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 160px;
  color: ${T9.slate500};
  font-size: 14px;
  svg {
    color: ${T9.slate400};
    animation: pdc-spin 1s linear infinite;
  }
  @keyframes pdc-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Table = styled.div`
  border-top: 1px solid ${T9.slate100};
  border-radius: 0 0 8px 8px;
  overflow-x: auto;
`;
const TableInner = styled.div`
  min-width: 720px;
`;
const Row = styled.div<{ template: string }>`
  display: grid;
  grid-template-columns: ${props => props.template};
  column-gap: 16px;
  align-items: center;
  padding: 0 8px 0 16px;
  min-height: 56px;
  border-bottom: 1px solid ${T9.slate100};
  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: ${T9.slate50};
  }
`;
const UserName = styled(Link)<{ disabled?: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-decoration: none;
  color: ${props => (props.disabled ? T9.slate500 : T9.slate900)};
  &:hover {
    color: ${T9.brand600};
    text-decoration: underline;
  }
`;
const RolesButton = styled.button<{ open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-height: 32px;
  padding: 3px 6px 3px 8px;
  border: 1px solid ${props => (props.open ? T9.accent500 : T9.slate300)};
  border-radius: 6px;
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 13px;
  color: ${T9.slate700};
  cursor: pointer;
  text-align: left;
  ${props =>
    props.open
      ? css`
          box-shadow: 0 0 0 2px ${T9.accent500};
        `
      : ""}
  &:hover:not(:disabled) {
    border-color: ${props => (props.open ? T9.accent500 : T9.slate400)};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  > svg {
    flex-shrink: 0;
    color: ${T9.slate500};
  }
`;
const RolePills = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
`;
const NoRoles = styled.span`
  color: ${T9.slate400};
`;
const RoleOption = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 6px 10px;
  border: 0;
  border-radius: 6px;
  background: ${props => (props.selected ? T9.brand50 : "transparent")};
  font: inherit;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  color: ${T9.slate700};
  cursor: pointer;
  &:hover {
    background: ${props => (props.selected ? T9.brand50 : T9.slate100)};
  }
  .check {
    width: 16px;
    display: inline-flex;
    color: ${T9.brand600};
  }
`;

const MEMBER_COLUMNS: ListColumn[] = [
  { key: "member", label: "Member", width: "minmax(220px, 1.4fr)" },
  { key: "roles", label: "Roles", width: "minmax(220px, 1.6fr)" },
  { key: "status", label: "Status", width: "120px" },
];

type RoleOptionType = { value: string; label: string };
type RolesSelectProps = {
  userRoles: RoleOptionType[];
  allRoles: RoleOptionType[];
  disabled?: boolean;
  onToggle: (roleId: string, toggled: boolean) => void;
};
/** The roles of one member: pills, and a checklist to add or remove a role. */
export const RolesSelect = ({ userRoles, allRoles, disabled, onToggle }: RolesSelectProps) => {
  const { open, setOpen, pos, anchorRef, panelRef } = usePopover("left");
  return (
    <>
      <RolesButton
        ref={anchorRef}
        type="button"
        open={open}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-test-id="projectDetails-roles"
        onClick={() => setOpen(!open)}
      >
        <RolePills>
          {userRoles.length ? (
            userRoles.map(r => (
              <Pill key={r.value} tone="brand">
                {r.label}
              </Pill>
            ))
          ) : (
            <NoRoles>No roles</NoRoles>
          )}
        </RolePills>
        <ChevronDown size={14} />
      </RolesButton>
      {open ? (
        <Portal>
          <MenuPanel
            ref={panelRef}
            role="listbox"
            aria-multiselectable
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            data-test-id="projectDetails-rolesMenu"
          >
            {allRoles.map(role => {
              const selected = Boolean(userRoles.find(r => r.value === role.value));
              return (
                <RoleOption
                  key={role.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  selected={selected}
                  data-test-id="projectDetails-roleOption"
                  onClick={() => onToggle(role.value, !selected)}
                >
                  <span className="check">{selected ? <Check size={14} /> : null}</span>
                  {role.label}
                </RoleOption>
              );
            })}
          </MenuPanel>
        </Portal>
      ) : null}
    </>
  );
};

type Props = {
  project: Project | null;
  loading: boolean;
  users: User[];
  usersLoading: boolean;
  roleAssignments: RoleAssignment[];
  roles: Role[];
  loggedUserId: string;
  onEnableUser: (user: User) => void;
  onRemoveUser: (user: User) => void;
  onUserRoleChange: (user: User, roleId: string, toggled: boolean) => void;
  onAddMemberClick: () => void;
  onDeleteClick: () => void;
};
type State = {
  showRemoveUserAlert: boolean;
};
@observer
class ProjectDetailsContent extends React.Component<Props, State> {
  state = {
    showRemoveUserAlert: false,
  };

  selectedUser: User | null = null;

  handleRemoveUserAction(user: User) {
    this.selectedUser = user;
    this.setState({ showRemoveUserAlert: true });
  }

  handleUserAction(user: User, item: { label: string; value: string }) {
    switch (item.value) {
      case "enable":
        this.props.onEnableUser(user);
        break;
      case "remove":
        this.handleRemoveUserAction(user);
        break;
      default:
        break;
    }
  }

  handleRemoveUserConfirmation() {
    if (this.selectedUser) {
      this.props.onRemoveUser(this.selectedUser);
    }

    this.setState({ showRemoveUserAlert: false });
  }

  handleCloseRemoveUserConfirmation() {
    this.setState({ showRemoveUserAlert: false });
  }

  renderLoading(what: string) {
    return (
      <Loading>
        <LoaderCircle size={20} />
        {what}
      </Loading>
    );
  }

  renderButtons() {
    if (this.props.loading) return null;

    return (
      <DetailsActions>
        <ActionGroup>
          <ActionButton onClick={this.props.onAddMemberClick}>
            <UserPlus size={16} />
            Add member
          </ActionButton>
        </ActionGroup>
        <ActionButton variant="danger" onClick={this.props.onDeleteClick}>
          <Trash2 size={16} />
          Delete project
        </ActionButton>
      </DetailsActions>
    );
  }

  renderInfo() {
    if (this.props.loading || !this.props.project) {
      return null;
    }
    const { project } = this.props;

    return (
      <SectionCard title="Overview" icon={Info} testId="projectDetails-overview">
        <KeyValueGrid
          columns={4}
          items={[
            { label: "Name", value: project.name ? this.renderValue(project.name) : null },
            {
              label: "Status",
              value: project.enabled ? (
                <Pill tone="success" icon={CircleCheck}>
                  Enabled
                </Pill>
              ) : (
                <Pill tone="neutral" icon={CircleX}>
                  Disabled
                </Pill>
              ),
            },
            {
              label: "Members",
              value: this.props.usersLoading ? null : String(this.props.users.length),
            },
            { label: "ID", value: this.renderValue(project.id), mono: true },
            {
              label: "Description",
              span: 4,
              value: project.description ? <CopyMultilineValue value={project.description} /> : null,
            },
          ]}
        />
      </SectionCard>
    );
  }

  renderUsers() {
    const projectId = this.props.project ? this.props.project.id : "";
    const getUserRoles = (user: RoleAssignment["user"]) =>
      this.props.roleAssignments
        .filter(a => a.scope.project && a.scope.project.id === projectId)
        .filter(a => a.user.id === user.id)
        .map(a => ({ value: a.role.id, label: a.role.name }));
    const hiddenRoles = configLoader.config.hiddenUserRoles || [];
    const allRoles = this.props.roles
      .filter(r => !hiddenRoles.includes(r.name))
      .map(r => ({ value: r.id, label: r.name }));
    const template = gridTemplate(MEMBER_COLUMNS);

    return (
      <Table>
        <TableInner>
          <ListHeader columns={MEMBER_COLUMNS} />
          {this.props.users.map(user => {
            const userRoles = getUserRoles(user);
            const isLoggedUser = user.id === this.props.loggedUserId;
            return (
              <Row key={user.id} template={template} data-test-id="projectDetails-member">
                <FlexCell gap={12}>
                  <IconTile icon={UserIcon} size={28} tone={user.enabled ? "brand" : "neutral"} />
                  <Stack>
                    <UserName disabled={!user.enabled} to={`/users/${user.id}`} title={user.name}>
                      {user.name}
                    </UserName>
                    {user.email ? <Secondary title={user.email}>{user.email}</Secondary> : null}
                  </Stack>
                </FlexCell>
                <div style={{ minWidth: 0 }}>
                  <RolesSelect
                    userRoles={userRoles}
                    allRoles={allRoles}
                    disabled={!user.enabled}
                    onToggle={(roleId, toggled) => {
                      this.props.onUserRoleChange(user, roleId, toggled);
                    }}
                  />
                </div>
                <div>
                  {user.enabled ? (
                    <Pill tone="success">Enabled</Pill>
                  ) : (
                    <Pill tone="neutral">Disabled</Pill>
                  )}
                </div>
                <div>
                  {!isLoggedUser ? (
                    <RowMenu
                      label={`Actions for ${user.name}`}
                      actions={[
                        {
                          label: `${user.enabled ? "Disable" : "Enable"} user`,
                          action: () => {
                            this.handleUserAction(user, { label: "", value: "enable" });
                          },
                        },
                        {
                          label: "Remove from project",
                          color: ThemePalette.alert,
                          action: () => {
                            this.handleUserAction(user, { label: "", value: "remove" });
                          },
                        },
                      ]}
                    />
                  ) : null}
                </div>
              </Row>
            );
          })}
        </TableInner>
      </Table>
    );
  }

  renderMembers() {
    if (this.props.loading) {
      return null;
    }
    const loadingUsers = this.props.usersLoading;
    const count = this.props.users.length;
    return (
      <SectionCard
        title={loadingUsers ? "Members" : `Members (${count})`}
        icon={Users}
        flush={!loadingUsers && count > 0}
        testId="projectDetails-members"
      >
        {loadingUsers ? (
          this.renderLoading("Loading the members…")
        ) : count ? (
          this.renderUsers()
        ) : (
          <EmptyState
            compact
            icon={Users}
            tone="neutral"
            title="No members yet"
            message="Add a user to this project and choose their roles."
            actionLabel="Add member"
            onAction={this.props.onAddMemberClick}
          />
        )}
      </SectionCard>
    );
  }

  renderValue(value: string) {
    return <CopyValue value={value} maxWidth="100%" />;
  }

  render() {
    return (
      <DetailsPage>
        {this.renderInfo()}
        {this.props.loading ? (
          <SectionCard testId="projectDetails-loading">
            {this.renderLoading("Loading the project…")}
          </SectionCard>
        ) : null}
        {this.renderMembers()}
        {this.renderButtons()}
        {this.state.showRemoveUserAlert ? (
          <AlertModal
            isOpen
            title="Remove user?"
            message="Are you sure you want to remove this user from the project?"
            extraMessage="They lose access to the project's transfers, deployments and endpoints."
            onConfirmation={() => {
              this.handleRemoveUserConfirmation();
            }}
            onRequestClose={() => {
              this.handleCloseRemoveUserConfirmation();
            }}
          />
        ) : null}
      </DetailsPage>
    );
  }
}

export default ProjectDetailsContent;
